/**
 * Minimal gsplat + GsplatRevealRadial — no entrance, no HUD gating, no overlays.
 *
 * Query params:
 *   ?revealOnly=1&world=lair           — test harness
 *   ?noReveal=1                        — splats only, no effect (sanity check)
 *   ?renderer=cpu                      — force CPU sort for comparison
 *   ?revealUrl=…lod-meta.json          — PlayCanvas streamed test asset
 */
import * as pc from 'playcanvas';
import { GsplatRevealRadial } from 'playcanvas/scripts/esm/gsplat/reveal-radial.mjs';
import {
  PLAYCANVAS_DESKTOP_PRESET,
  mergePlayCanvasLodPreset,
  type PlayCanvasLodPreset,
} from '@viewer/lod/streamingProfiles';
import type { LoadedScene } from '@viewer/world/worldTypes';

const SPLAT_READY_MIN = 10_000;

export interface MountRevealOnlyOptions {
  splatUrl: string;
  cameraStart: [number, number, number];
  cameraYawDeg?: number;
  worldScene?: LoadedScene;
  layoutRoot?: HTMLElement;
  revealCenter?: [number, number, number];
}

export interface RevealOnlyViewer {
  resize: () => void;
  dispose: () => void;
}

type GsplatLodComponent = pc.GSplatComponent & {
  lodRangeMin: number;
  lodRangeMax: number;
  lodBaseDistance: number;
  lodMultiplier: number;
};

type GsplatOctreeResource = { octree?: { lodLevels?: number } } | null | undefined;

function gsplatLod(comp: pc.GSplatComponent): GsplatLodComponent {
  return comp as GsplatLodComponent;
}

function resolveGsplatEuler(worldScene?: LoadedScene): [number, number, number] {
  const [rx, ry, rz] = worldScene?.rotation ?? [0, 0, 0];
  const basisX = worldScene?.playCanvasBasis === false ? 0 : 180;
  return [basisX + rx, ry, rz];
}

function computeGsplatFovScale(camera: pc.CameraComponent): number {
  const DEG_TO_RAD = Math.PI / 180;
  const REF_TAN_HALF_FOV = Math.tan(22.5 * DEG_TO_RAD);
  let tanHalfVFov = Math.tan(camera.fov * 0.5 * DEG_TO_RAD);
  if (camera.horizontalFov) tanHalfVFov /= camera.aspectRatio;
  const tanHalfHFov = tanHalfVFov * camera.aspectRatio;
  return Math.max(tanHalfHFov, tanHalfVFov) / REF_TAN_HALF_FOV;
}

function resolveLodRange(
  lodPreset: PlayCanvasLodPreset,
  resource: GsplatOctreeResource,
): { min: number; max: number } {
  const maxLod =
    resource?.octree?.lodLevels != null && resource.octree.lodLevels > 0
      ? resource.octree.lodLevels - 1
      : lodPreset.lodRangeMax;
  const max = Math.max(0, Math.min(lodPreset.lodRangeMax, maxLod));
  const min = Math.max(0, Math.min(lodPreset.lodRangeMin, max));
  return { min, max };
}

function applyComponentLodRange(
  gsplatComp: pc.GSplatComponent,
  lodPreset: PlayCanvasLodPreset,
  resource: GsplatOctreeResource,
): void {
  const gs = gsplatLod(gsplatComp);
  const { min, max } = resolveLodRange(lodPreset, resource);
  gs.lodRangeMin = min;
  gs.lodRangeMax = max;
}

function applyComponentLodDistances(
  gsplatComp: pc.GSplatComponent,
  camera: pc.CameraComponent,
  lodPreset: PlayCanvasLodPreset,
): void {
  const gs = gsplatLod(gsplatComp);
  gs.lodBaseDistance = lodPreset.lodBaseDistance * computeGsplatFovScale(camera);
  gs.lodMultiplier = lodPreset.lodMultiplier;
}

function applySceneGsplatDefaults(
  app: pc.AppBase,
  preset: PlayCanvasLodPreset,
  worldScene?: LoadedScene,
): void {
  const gs = app.scene.gsplat;
  gs.lodUpdateAngle = preset.lodUpdateAngle;
  gs.lodBehindPenalty = preset.lodBehindPenalty;
  gs.lodUpdateDistance = preset.lodUpdateDistance;
  gs.lodUnderfillLimit = preset.lodUnderfillLimit;
  gs.radialSorting = preset.radialSorting;
  gs.dataFormat = pc.GSPLATDATA_COMPACT;
  gs.minPixelSize = preset.minPixelSize;
  gs.alphaClipForward = 1 / 255;
  gs.minContribution = preset.minContribution;
  gs.alphaClip = 0.2;
  const sceneLod = worldScene?.lodSplatCount;
  gs.splatBudget =
    typeof sceneLod === 'number' && sceneLod > 0
      ? sceneLod
      : Math.round(preset.splatBudgetMillions * 1_000_000);
}

function describeGsplatRenderer(app: pc.AppBase): string {
  const r = app.scene.gsplat.currentRenderer;
  if (r === pc.GSPLAT_RENDERER_COMPUTE) return 'compute';
  if (r === pc.GSPLAT_RENDERER_RASTER_GPU_SORT) return 'raster-gpu';
  if (r === pc.GSPLAT_RENDERER_RASTER_CPU_SORT) return 'cpu';
  return 'auto';
}

function resolveRevealCenter(
  worldScene: LoadedScene | undefined,
  cameraStart: [number, number, number],
  override?: [number, number, number],
): [number, number, number] {
  if (override) return override;
  const callout = worldScene?.labels?.find((l) => l.id === 'entrance-callout');
  if (callout) return callout.position;
  return cameraStart;
}

export async function mountRevealOnlyViewer(
  container: HTMLElement,
  options: MountRevealOnlyOptions,
): Promise<RevealOnlyViewer> {
  const params = new URLSearchParams(window.location.search);
  const noReveal = params.get('noReveal') === '1';
  const rendererParam = params.get('renderer');

  const canvas = document.createElement('canvas');
  canvas.className = 'gii-pc-canvas';
  canvas.style.display = 'block';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  container.appendChild(canvas);

  const hud = document.createElement('div');
  hud.className = 'gii-reveal-only-hud';
  hud.setAttribute('aria-live', 'polite');
  container.appendChild(hud);

  const layoutRoot = options.layoutRoot ?? container;
  const preset = mergePlayCanvasLodPreset(
    PLAYCANVAS_DESKTOP_PRESET,
    options.worldScene?.lod,
    undefined,
  );

  let device: pc.GraphicsDevice;
  try {
    device = await pc.createGraphicsDevice(canvas, {
      deviceTypes: [pc.DEVICETYPE_WEBGPU],
      antialias: false,
      powerPreference: 'high-performance',
    });
  } catch {
    device = await pc.createGraphicsDevice(canvas, {
      deviceTypes: [pc.DEVICETYPE_WEBGL2],
      antialias: false,
    });
  }

  const app = new pc.AppBase(canvas);
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  device.maxPixelRatio = dpr;

  const applyLayout = (): boolean => {
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    const cw = canvas.clientWidth;
    const ch = canvas.clientHeight;
    if (cw < 1 || ch < 1) return false;
    device.resizeCanvas(cw, ch);
    device.updateClientRect();
    const cam = cameraEntity.camera;
    if (cam) {
      cam.aspectRatio = cw / ch;
      if (gsplatComp) applyComponentLodDistances(gsplatComp, cam, preset);
    }
    return true;
  };

  const resize = () => {
    applyLayout();
  };

  let cameraEntity!: pc.Entity;
  let gsplatComp: pc.GSplatComponent | null = null;

  const createOptions = new pc.AppOptions();
  createOptions.graphicsDevice = device;
  createOptions.componentSystems = [
    pc.CameraComponentSystem,
    pc.GSplatComponentSystem,
    pc.ScriptComponentSystem,
  ];
  // SOG bundles unpack into internal `texture`-type sub-assets (means/scales/quats/sh0/shN).
  // The SogBundleParser uses Promise.allSettled, so the gsplat `asset.on('load')` fires even
  // when those texture loads fail for lack of a handler — yielding a 200 OK download but
  // splats=0 (null GPU textures). TextureHandler/ContainerHandler must be registered too,
  // exactly as the main mountPlayCanvasViewer does.
  createOptions.resourceHandlers = [
    pc.TextureHandler,
    pc.ContainerHandler,
    pc.GSplatHandler,
  ];
  app.init(createOptions);
  app.setCanvasFillMode(pc.FILLMODE_NONE);
  app.setCanvasResolution(pc.RESOLUTION_AUTO);
  (app as unknown as Record<string, unknown>)._allowResize = false;
  app.autoRender = true;

  applySceneGsplatDefaults(app, preset, options.worldScene);

  // Engine 2.20.0: AUTO → hybrid GPU-sort on WebGPU (applies the reveal's gsplatModifyVS
  // chunk), CPU sort on WebGL2. `?renderer=cpu` forces CPU sort for comparison.
  const gs = app.scene.gsplat;
  gs.renderer =
    rendererParam === 'cpu' ? pc.GSPLAT_RENDERER_RASTER_CPU_SORT : pc.GSPLAT_RENDERER_AUTO;

  cameraEntity = new pc.Entity('camera');
  cameraEntity.addComponent('camera', {
    clearColor: new pc.Color(0, 0, 0, 1),
    fov: 65,
    nearClip: 0.1,
    farClip: 5000,
  });
  app.root.addChild(cameraEntity);
  const [cx, cy, cz] = options.cameraStart;
  cameraEntity.setLocalPosition(cx, cy, cz);
  cameraEntity.setLocalEulerAngles(0, options.cameraYawDeg ?? 0, 0);

  const asset = new pc.Asset('gsplat', 'gsplat', { url: options.splatUrl });
  app.assets.add(asset);
  await new Promise<void>((resolve, reject) => {
    asset.on('load', () => resolve());
    asset.on('error', (err: string) => reject(new Error(err)));
    app.assets.load(asset);
  });

  const resource = asset.resource as GsplatOctreeResource;
  const cam = cameraEntity.camera!;

  const gsplatEntity = new pc.Entity('gsplat');
  gsplatEntity.addComponent('gsplat', { asset, unified: true });
  const ws = options.worldScene;
  if (ws) {
    gsplatEntity.setLocalPosition(ws.position[0], ws.position[1], ws.position[2]);
    const s = ws.scale;
    const scale = typeof s === 'number' ? s : 1;
    gsplatEntity.setLocalScale(scale, scale, scale);
  }
  const [ex, ey, ez] = resolveGsplatEuler(ws);
  gsplatEntity.setLocalEulerAngles(ex, ey, ez);
  app.root.addChild(gsplatEntity);

  gsplatComp = gsplatEntity.gsplat!;
  applyComponentLodRange(gsplatComp, preset, resource);
  applyComponentLodDistances(gsplatComp, cam, preset);
  // Time-varying reveal: the gsplatModifyVS chunk runs when splats are written to the work
  // buffer, so the hybrid GPU-sort renderer must re-render it every frame or the wave freezes.
  // Harmless on CPU sort.
  if (!noReveal) {
    (gsplatComp as unknown as { workBufferUpdate?: number }).workBufferUpdate =
      pc.WORKBUFFER_UPDATE_ALWAYS;
  }

  const [fx, fy, fz] = resolveRevealCenter(ws, options.cameraStart, options.revealCenter);

  let reveal: GsplatRevealRadial | null = null;
  let revealArmed = noReveal;
  let revealStarted = false;

  if (!noReveal) {
    gsplatEntity.addComponent('script');
    reveal = gsplatEntity.script!.create(
      GsplatRevealRadial as unknown as typeof pc.Script,
    ) as unknown as GsplatRevealRadial;
    reveal.enabled = false;
    reveal.center.set(fx, fy, fz);
    reveal.speed = 5;
    reveal.acceleration = 0;
    reveal.delay = 2;
    reveal.oscillationIntensity = 0.2;
    reveal.endRadius = 50;
    reveal.bandWidth = 1;
  }

  console.info('[GII reveal-only] mounted', {
    noReveal,
    requestedRenderer: rendererParam ?? 'cpu',
    activeRenderer: describeGsplatRenderer(app),
    graphics: device.isWebGPU ? 'webgpu' : 'webgl2',
    centerWorld: `${fx.toFixed(2)}, ${fy.toFixed(2)}, ${fz.toFixed(2)}`,
    lodRange: `${gsplatLod(gsplatComp).lodRangeMin}–${gsplatLod(gsplatComp).lodRangeMax}`,
    splatBudget: gs.splatBudget,
    completionS: reveal?.getCompletionTime(),
  });

  app.on('update', () => {
    const splats = app.stats.frame.gsplats ?? 0;

    if (reveal && !revealArmed && splats >= SPLAT_READY_MIN) {
      revealArmed = true;
      reveal.enabled = true;
      revealStarted = true;
      console.info('[GII reveal-only] splats ready — reveal START', { splats });
    }

    const phase = noReveal
      ? 'no-reveal'
      : !revealArmed
        ? 'waiting-splats'
        : revealStarted && reveal!.enabled
          ? 'revealing'
          : 'done';

    hud.textContent =
      `REVEAL ONLY · ${phase} · renderer=${describeGsplatRenderer(app)}` +
      ` · splats=${splats.toLocaleString()}` +
      (reveal
        ? ` · t=${reveal.effectTime.toFixed(2)}s/${reveal.getCompletionTime().toFixed(1)}s` +
          ` · shader=${reveal.material ? 'yes' : 'no'}`
        : '');
  });

  const ro = new ResizeObserver(() => resize());
  ro.observe(layoutRoot);
  resize();
  app.start();

  return {
    resize,
    dispose: () => {
      ro.disconnect();
      gsplatEntity.destroy();
      app.assets.remove(asset);
      asset.unload();
      app.destroy();
      hud.remove();
      canvas.remove();
      device.destroy();
    },
  };
}
